import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { ModelContext } from "../../rdf/model";
import { countTermsByDataset } from "../../rdf/model";
import type { DatasetDescriptor } from "../../rdf/types";
import { resolveVersion } from "../../rdf/types";
import { useAppState } from "../../state/AppStateContext";
import { useUi } from "../../state/ui";
import { findOntologyTerm } from "./dataset-info";

interface OntologiesTabProps {
  ctx: ModelContext;
  datasets: DatasetDescriptor[];
  activeDatasetIds: ReadonlySet<string>;
  onSetDatasetActive: (datasetId: string, active: boolean) => void;
}

export default function OntologiesTab({
  ctx,
  datasets,
  activeDatasetIds,
  onSetDatasetActive,
}: OntologiesTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { versionSelections, setDatasetVersion, removeUserOntology } = useAppState();
  const { openUpload } = useUi();
  const termCounts = useMemo(() => countTermsByDataset(ctx), [ctx]);

  const selectedId = searchParams.get("dataset") ?? datasets[0]?.id ?? "";
  const selected = datasets.find((d) => d.id === selectedId) ?? datasets[0] ?? null;
  const selectedOntologyTerm = selected ? findOntologyTerm(ctx, selected) : null;
  const selectedVersion = selected ? resolveVersion(selected, versionSelections) : null;

  const bundled = datasets.filter((d) => d.origin === "bundled");
  const uploaded = datasets.filter((d) => d.origin === "user");

  const selectDataset = (id: string) => setSearchParams({ dataset: id });

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex w-80 shrink-0 flex-col overflow-y-auto border-r border-[var(--oe-border)] bg-[var(--oe-surface)]">
        <DatasetGroup
          title="Bundled"
          datasets={bundled}
          selectedId={selected?.id ?? ""}
          activeDatasetIds={activeDatasetIds}
          termCounts={termCounts}
          onSelect={selectDataset}
          onSetActive={onSetDatasetActive}
        />
        <DatasetGroup
          title="Your uploads"
          datasets={uploaded}
          selectedId={selected?.id ?? ""}
          activeDatasetIds={activeDatasetIds}
          termCounts={termCounts}
          onSelect={selectDataset}
          onSetActive={onSetDatasetActive}
        />
        <div className="p-3">
          <button
            type="button"
            onClick={openUpload}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--oe-border-strong)] px-3 py-2.5 text-sm font-medium text-[var(--oe-text-muted)] transition-colors hover:border-[var(--oe-accent)] hover:text-[var(--oe-accent)]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Upload ontology…
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {selected && selectedVersion ? (
          <div className="max-w-2xl">
            <div className="mb-1.5 flex items-center gap-2.5">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: selected.accentColor }}
              />
              <h1 className="text-xl font-semibold tracking-tight">{selected.label}</h1>
              {selected.origin === "user" && (
                <span className="rounded-full border border-[var(--oe-border)] px-2 py-0.5 text-[11px] text-[var(--oe-text-muted)]">
                  uploaded
                </span>
              )}
            </div>
            <p className="mb-6 text-sm leading-relaxed text-[var(--oe-text-muted)]">
              {selected.description}
            </p>

            <section className="mb-6 rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)] p-4">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
                Version
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selectedVersion.id}
                  disabled={selected.versions.length === 1}
                  onChange={(event) => setDatasetVersion(selected.id, event.target.value)}
                  className="rounded-lg border border-[var(--oe-border)] bg-[var(--oe-bg)] px-3 py-1.5 text-sm text-[var(--oe-text)] focus:border-[var(--oe-accent)] focus:outline-none disabled:cursor-default disabled:opacity-70"
                >
                  {selected.versions.map((version) => (
                    <option key={version.id} value={version.id}>
                      {version.label}
                      {version.releaseDate ? ` · ${version.releaseDate}` : ""}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-[var(--oe-text-muted)]">
                  {selected.versions.length === 1
                    ? "Only version available — the switcher activates as more versions are added."
                    : `${selected.versions.length} versions available`}
                </span>
              </div>
              {selectedVersion.note && (
                <p className="mt-2 text-xs text-[var(--oe-text-muted)]">{selectedVersion.note}</p>
              )}
            </section>

            <dl className="grid grid-cols-[130px_1fr] gap-y-2.5 text-sm">
              <dt className="text-[var(--oe-text-muted)]">Namespace</dt>
              <dd className="break-all font-mono text-xs leading-5">{selected.namespaceIri || "—"}</dd>

              <dt className="text-[var(--oe-text-muted)]">Source</dt>
              <dd>
                {selected.sourceUrl ? (
                  <a
                    href={selected.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all text-[var(--oe-accent)] hover:underline"
                  >
                    {selected.sourceUrl}
                  </a>
                ) : (
                  "Uploaded file"
                )}
              </dd>

              <dt className="text-[var(--oe-text-muted)]">License</dt>
              <dd>{selected.license}</dd>

              <dt className="text-[var(--oe-text-muted)]">Terms indexed</dt>
              <dd>{(termCounts.get(selected.id) ?? 0).toLocaleString()}</dd>

              <dt className="text-[var(--oe-text-muted)]">Files</dt>
              <dd>
                <ul className="space-y-0.5">
                  {selectedVersion.files.map((file) => (
                    <li key={file.id} className="font-mono text-xs">
                      {file.fileName}
                    </li>
                  ))}
                </ul>
              </dd>

              {selectedOntologyTerm?.comment && (
                <>
                  <dt className="text-[var(--oe-text-muted)]">Self-description</dt>
                  <dd className="leading-relaxed text-[var(--oe-text-muted)]">
                    {selectedOntologyTerm.comment}
                  </dd>
                </>
              )}
            </dl>

            {selected.origin === "user" && (
              <div className="mt-8 border-t border-[var(--oe-border)] pt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove "${selected.label}" from your workspace? The uploaded file will be deleted from this browser.`,
                      )
                    ) {
                      void removeUserOntology(selected.id);
                      setSearchParams({});
                    }
                  }}
                  className="rounded-lg border border-[var(--oe-danger)]/40 px-3 py-1.5 text-sm font-medium text-[var(--oe-danger)] transition-colors hover:bg-[var(--oe-danger)]/10"
                >
                  Remove ontology
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[var(--oe-text-muted)]">
            Select an ontology to see its details.
          </p>
        )}
      </div>
    </div>
  );
}

function DatasetGroup({
  title,
  datasets,
  selectedId,
  activeDatasetIds,
  termCounts,
  onSelect,
  onSetActive,
}: {
  title: string;
  datasets: DatasetDescriptor[];
  selectedId: string;
  activeDatasetIds: ReadonlySet<string>;
  termCounts: Map<string, number>;
  onSelect: (id: string) => void;
  onSetActive: (id: string, active: boolean) => void;
}) {
  if (datasets.length === 0) return null;
  return (
    <div className="pt-3">
      <p className="px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
        {title}
      </p>
      <ul>
        {datasets.map((dataset) => (
          <li key={dataset.id}>
            <div
              className={`mx-2 flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm transition-colors ${
                selectedId === dataset.id
                  ? "bg-[var(--oe-accent-soft)] text-[var(--oe-accent)]"
                  : "hover:bg-[var(--oe-hover)]"
              }`}
              onClick={() => onSelect(dataset.id)}
            >
              <input
                type="checkbox"
                checked={activeDatasetIds.has(dataset.id)}
                onChange={(event) => {
                  event.stopPropagation();
                  onSetActive(dataset.id, event.target.checked);
                }}
                onClick={(event) => event.stopPropagation()}
                className="accent-[var(--oe-accent)]"
                aria-label={`Toggle ${dataset.shortLabel} active`}
              />
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: dataset.accentColor }}
                aria-hidden="true"
              />
              <span className="flex-1 truncate font-medium">{dataset.shortLabel}</span>
              <span className="text-xs text-[var(--oe-text-muted)]">
                {termCounts.get(dataset.id) ?? 0}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
